import React from 'react';
import {HttpLink, ApolloLink, Observable} from '@apollo/client';
import {onError} from '@apollo/client/link/error';
import {setContext} from '@apollo/client/link/context';
import {captureException} from '@sentry/react';
import {fetchAuthSession} from 'aws-amplify/auth';
import * as Sentry from '@sentry/react';
import {SnackbarAction, SnackbarKey} from 'notistack';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import {toast} from './utils/snackbar';

const BACKEND_API_PORT = 8000;
const BACKEND_URL = `${process.env.VITE_APP_BACKEND_URL}`;

const abortController = new AbortController();

export const httpLink = new HttpLink({
  uri: (_operation) => {
    return (
      BACKEND_URL || `https://${window.location.hostname}:${BACKEND_API_PORT}/graphql/`
    );
  },
  fetchOptions: {
    signal: abortController.signal, // added for sentry HTTP capturing https://docs.sentry.io/platforms/javascript/session-replay/troubleshooting/
  },
});

export const errorLink = onError(({graphQLErrors, networkError, operation, forward}) => {
  const graphErrors = [];

  if (networkError) {
    graphErrors.push(
      `[Network error]: ${operation.operationName} failed with ${networkError}`
    );

    // Show error page when OneView API returns HTTP 403 Forbidden for any request
    if (
      !!networkError &&
      typeof networkError === 'object' && // networkError sometimes is a string
      'statusCode' in networkError &&
      networkError.statusCode === 403
    ) {
      // networkErrorEvent will be picked up by Layout.tsx later
      const networkErrorEvent = new CustomEvent('networkErrorEvent', {
        detail: {...networkError},
      });
      toast('You do not have permission to perform this action.', 'error');
      dispatchEvent(networkErrorEvent);
    }
  }

  if (graphQLErrors) {
    // eslint-disable-next-line consistent-return
    graphQLErrors.forEach((err) => {
      // Retry for cases such as expired auth tokens
      switch (err.extensions?.code) {
        case 'UNAUTHENTICATED':
          return forward(operation);
        default: {
          let locationSection = '';
          let pathSection = '';
          if (err.locations) {
            const locations = err.locations
              .map((location) => `\tline: ${location.line}, column: ${location.column}`)
              .join('\n');
            locationSection = `\nLocations:\n${locations}`;
          }
          if (err.path) {
            const paths = err.path.map((path) => `\tpath: ${path}`).join('\n');
            pathSection = `\nPaths:\n${paths}`;
          }
          const graphQLErrorMessage = `[GraphQL error]:\nMessage: ${err.message}${pathSection}${locationSection}`;
          graphErrors.push(graphQLErrorMessage);
          toast(graphQLErrorMessage, 'error');
        }
      }
    });
  }

  graphErrors.forEach((err) => {
    // eslint-disable-next-line no-console
    console.error(err);
  });
  return forward(operation);
});

const refreshPageAction: SnackbarAction = (_key: SnackbarKey) => {
  return <Button onClick={() => window.location.reload()}>Refresh page</Button>;
};

// Global event to trigger session expired dialog
const triggerSessionExpiredDialog = () => {
  const sessionExpiredEvent = new CustomEvent('sessionExpiredEvent');
  dispatchEvent(sessionExpiredEvent);
};

// Session Expired Dialog Component
export const SessionExpiredDialog: React.FC = () => {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const handleSessionExpired = () => {
      setOpen(true);
    };

    window.addEventListener('sessionExpiredEvent', handleSessionExpired);
    return () => {
      window.removeEventListener('sessionExpiredEvent', handleSessionExpired);
    };
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Dialog open={open} disableEscapeKeyDown>
      <DialogTitle>Session Expired</DialogTitle>
      <DialogContent>
        <Typography>Your session has expired. Please log in again.</Typography>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{mr: 2, mb: 2}}
          onClick={handleRefresh}
          variant="contained"
          color="primary"
        >
          Log in
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const authLink = setContext(
  async ({operationName, variables, context}, {headers}) => {
    try {
      // in e2e tests, we use ApiToken for authentication instead of
      // JwtToken
      let token = localStorage.getItem('ApiToken');
      if (token == null || !token) {
        try {
          const thisAuthSession = await fetchAuthSession();
          Sentry.setContext('idToken', thisAuthSession?.tokens?.idToken || null);

          const accessToken = thisAuthSession?.tokens?.accessToken;
          if (accessToken) {
            token = accessToken.toString();
          }
          if (!token || token === 'undefined' || token === 'null') {
            // This time, force refresh the auth session to ensure we get a valid token.
            const {tokens: tokensTryAgain} = await fetchAuthSession({forceRefresh: true});
            const accessTokenTryAgain = tokensTryAgain?.accessToken;
            if (accessTokenTryAgain) {
              token = accessTokenTryAgain.toString();
            } else {
              // If no token is found, we can assume the user is not authenticated
              // and need to tell the user to log in again.
              toast(
                'No authentication token found after calling aws-amplify fetchAuthSession(). Please log in again.',
                'error',
                refreshPageAction
              );
            }
          }
        } catch (err) {
          // If we can't set the context, we can ignore it. Just used for debugging.
        }
      }

      return {
        headers: {
          ...headers,
          frontendUrl: window.location.href,
          // authorization: null, // for testing
          authorization: token ? `bearer ${token}` : null,
        },
      };
    } catch (err) {
      toast(
        'Your login has expired. Please refresh the page to avoid losing your current work.',
        'error',
        refreshPageAction
      );
      captureException(err, {
        data: {
          operationName,
          variables,
          context,
        },
      });

      // Return empty headers without authorization in case of error
      return {
        headers: {
          ...headers,
          frontendUrl: window.location.href,
          authorization: null,
        },
      };
    }
  }
);

// Custom Apollo Link that cancels requests if no authorization header is present
// This link should be placed after authLink but before httpLink in the Apollo client chain
// It prevents unnecessary network requests when the user is not authenticated
export const authorizationCheckLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext();
  const headers = context.headers || {};

  // Check if authorization header is present and not null
  if (
    !headers.authorization ||
    headers.authorization === 'null' ||
    headers.authorization === null
  ) {
    // Trigger session expired dialog
    triggerSessionExpiredDialog();

    // Return an observable that never emits anything to cancel the request
    return new Observable(() => {
      // This observable never calls observer.next(), observer.error(), or observer.complete()
      // which effectively cancels the request
    });
  }

  // If authorization header is present, continue with the request
  return forward(operation);
});
